import {Location} from '@angular/common'
import {Component, inject, OnInit, signal} from '@angular/core'
import {MatButton, MatIconButton} from '@angular/material/button'
import {MatDialog} from '@angular/material/dialog'
import {MatIcon} from '@angular/material/icon'
import {ActivatedRoute} from '@angular/router'
import {APPLICATION_ID} from '@sparbanken-syd/loan-backend'
import {ConfigService} from '@sparbanken-syd/sparbanken-syd-bankid'
import {WaitComponent} from '@sparbanken-syd/sparbanken-syd-theme'
import {filter, finalize, tap} from 'rxjs'
import {StatusIndicatorComponent} from '../../components/status-indicator/status-indicator.component'
import {LoanService} from '../../services/loan.service'
import {RoleHelper} from '../../services/role-helper'
import {BasicInfoComponent} from './0-basic-info/basic-info.component'
import {NewPropertyComponent} from './2-new-property/new-property.component'
import {LoanCostsComponent} from './3-loan-costs/loan-costs.component'
import {OldPropertyComponent} from './4-old-property/old-property.component'
import {TaxReductionComponent} from './5-tax-reduction/tax-reduction.component'
import {KalpInfoComponent} from './6-kalp-info/kalp-info.component'
import {UcInfoComponent} from './7-uc-info/uc-info.component'
import {AddNotesDialogData, KalpAddNotesComponent} from './kalp-add-notes/kalp-add-notes.component'
import {KalpEditingComponent} from './kalp-editing/kalp-editing.component'
import {KalpIssueLoanComponent} from './kalp-issue-loan/kalp-issue-loan.component'
import {KalpLogsComponent} from './kalp-logs/kalp-logs.component'
import {KalpNonSuperfastInfoComponent} from './kalp-non-superfast-info/kalp-non-superfast-info.component'
import {KalpStatusProgressComponent} from './kalp-status-progress/kalp-status-progress.component'
import {KalpUcResultComponent} from './kalp-uc-result/kalp-uc-result.component'
import {map, switchMap} from 'rxjs/operators'
import {WINDOW} from '../../application/shared'

@Component({
  selector: 'spb-kalp',
  templateUrl: './kalp.component.html',
  styleUrls: ['./kalp.component.scss'],
  imports: [
    MatButton,
    MatIcon,
    StatusIndicatorComponent,
    KalpNonSuperfastInfoComponent,
    KalpStatusProgressComponent,
    KalpLogsComponent,
    BasicInfoComponent,
    NewPropertyComponent,
    LoanCostsComponent,
    OldPropertyComponent,
    TaxReductionComponent,
    KalpInfoComponent,
    UcInfoComponent,
    MatIconButton
  ],
  providers: [{provide: WINDOW, useValue: globalThis}]
})
export class KalpComponent implements OnInit {
  public readonly location = inject(Location)
  public readonly dialog = inject(MatDialog)
  public readonly loanService = inject(LoanService)
  public readonly helper = inject(RoleHelper)
  protected readonly configService = inject(ConfigService)
  private readonly route = inject(ActivatedRoute)

  private readonly loanId = signal('')

  private readonly window = inject(WINDOW)

  public ngOnInit() {
    this.route.paramMap.pipe(
      map(params => params.get('id')),
      filter(Boolean),
      tap(id => this.loanId.set(id)),
      switchMap(id => this.loanService.fetchComplete(id))
    ).subscribe()
  }

  public startEditing() {
    this.dialog
      .open(KalpEditingComponent, {
        disableClose: true,
        width: '600px'
      })
  }

  public reRunUc(): void {
    const waitDialogRef = this.dialog.open(WaitComponent, {
      data: {title: 'Gör UC kontroll', progress: true}
    })

    this.loanService.runUc(this.loanId()).pipe(
      finalize(() => waitDialogRef.close())
    ).subscribe({
      next: () => this.dialog.open(KalpUcResultComponent),
      error: () => this.dialog.open(WaitComponent, {
        data: {
          title: 'Någonting gick fel',
          text: 'Felaktigt svar från UC',
          closable: true
        }
      })
    })
  }

  public declineLoan() {
    const ref = this.dialog.open(KalpIssueLoanComponent, {
      data: {type: 'Avslå', autoComment: 'Lånelöftet har fått avslag'},
      disableClose: true
    })

    ref.afterClosed()
      .pipe(filter(Boolean))
      .subscribe({
        next: (notes: string) => {
          const waitRef = this.dialog.open(WaitComponent, {
            data: {title: 'Avslår', progress: true}
          })

          this.loanService.declineLoan(this.loanId(), notes).subscribe({
            next: () => {
              waitRef.close()
            }
          })
        }
      })
  }

  public issueLoan() {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Beviljar', progress: true}
    })

    const ref = this.dialog.open(KalpIssueLoanComponent, {
      data: {type: 'Bevilja', autoComment: 'Lånelöfte beviljat'},
      disableClose: true
    })

    ref.afterClosed()
      .pipe(filter(Boolean))
      .subscribe({
        next: (notes: string) => {
          this.loanService.issueLoan(this.loanId(), notes).subscribe({
            next: () => {
              waitRef.close()
              ref.close()
            }
          })
        }
      })
  }


  public addNote() {
    this.dialog.open<KalpAddNotesComponent, AddNotesDialogData, any>(KalpAddNotesComponent, {
      disableClose: true,
      data: {
        applicationId: this.loanId(),
        logType: 'note',
        resolved: false,
        previousNotes: []
      }
    })
  }

  public openDocument() {
    this.loanService.fetchLoanDocument(this.loanService.application()![APPLICATION_ID])
      .subscribe({
        next: url => this.window.open(url, '_blank')
      })
  }
}
